import NoImage from 'assets/images/noimage.png'
import LoadingV2 from 'components/LoadingV2'
import PMBModalImagePreview from 'components/PMBModalImagePreview'
import PMBModalRemove from 'components/PMBModalRemove'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import { useDebounce } from 'helpers'
import useIsMobile from 'hooks/useIsMobile'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { ChevronDown, Edit2, Plus, Trash2 } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Spinner } from 'reactstrap'
import ModalAddEditMasterItem from '../master-items/ModalAddEditMasterItem'
import {
  useDeleteMasterItemMutation,
  useFetchMasterItemsQuery,
} from '../master-items/masterItemsApiSlice'
import { setFetchOption } from '../master-items/masterItemsSlice'

const TabMasterItems = () => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const { tag } = useSelector((state) => state.masterItems)

  const isMobile = useIsMobile()

  const [searchInput, setSearchInput] = useState('')
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const [isFilter, setIsFilter] = useState(false)
  const [imageItems, setImagesItems] = useState([])
  const [modalOpenImage, setModalOpenImage] = useState(false)
  const [isOpenModalAddEdit, setIsOpenModalAddEdit] = useState(false)
  const [dataItemById, setDataItemById] = useState(null)

  const [deleteMasterItem, { isLoading: isDeleting }] =
    useDeleteMasterItemMutation()

  const { data, isLoading, refetch, isFetching } = useFetchMasterItemsQuery(
    {
      page: tag?.page ?? 0,
      size: tag?.size ?? 10,
      s: tag?.s,
      sortby: tag?.sortby,
      sortdir: tag?.sortdir,
    },
    { refetchOnMountOrArgChange: true, skip: false },
  )

  // Separate query for parent items dropdown with reasonable limit
  const { data: parentItemsData } = useFetchMasterItemsQuery(
    {
      page: 0,
      size: 100,
      sortby: 'name',
      sortdir: 'ASC',
    },
    { refetchOnMountOrArgChange: false },
  )

  const onHandleOpenImageModal = (imageUrls) => {
    setImagesItems([imageUrls])
    setModalOpenImage(true)
  }

  // COLUMNS
  const columns = [
    {
      name: <span>Gambar</span>,
      selector: 'picture',
      width: '100px',
      cell: (row, index) => {
        return (
          <img
            src={
              row?.picture && row?.picture.length > 0 ? row?.picture : NoImage
            }
            alt={row?.name || `item ${index + 1}`}
            width={48}
            height={48}
            className="rounded"
            onClick={() => onHandleOpenImageModal(row?.picture || NoImage)}
            style={{ cursor: 'pointer', objectFit: 'cover' }}
          />
        )
      },
    },
    {
      name: <span>Kode SKU</span>,
      selector: 'code',
      sortable: true,
      sortField: 'code',
      grow: 1,
      cell: (row) => {
        return (
          <span className="m-0 fw-semibold" data-testid={`findora-items-row-${row.id}`}>
            {row?.code}
          </span>
        )
      },
    },
    {
      name: <span>Nama Barang</span>,
      selector: 'name',
      sortable: true,
      sortField: 'name',
      grow: 2,
      cell: (row) => {
        return <span className="m-0">{row?.name}</span>
      },
    },
    {
      name: <span>Kategori</span>,
      selector: 'category',
      grow: 1,
      cell: (row) => {
        return <span className="m-0">{row?.category || '-'}</span>
      },
    },
    {
      name: <span>Nama Ilmiah</span>,
      selector: 'scientificName',
      grow: 1,
      cell: (row) => {
        return <span className="m-0">{row?.scientificName || '-'}</span>
      },
    },
    {
      name: <span>Parent Item</span>,
      selector: 'parentName',
      grow: 1,
      cell: (row) => {
        return <span className="m-0">{row?.parentName || '-'}</span>
      },
    },
    {
      name: <span>Dibuat Oleh</span>,
      selector: 'createdBy',
      grow: 1,
      cell: (row) => {
        return (
          <div className="d-flex flex-column">
            <span className="m-0 fw-semibold">{row?.createdBy || '-'}</span>
            <span className="m-0 text-muted" style={{ fontSize: '0.8rem' }}>
              {row?.createdAt
                ? moment(row.createdAt).format('DD MMM YYYY, HH:mm')
                : '-'}
            </span>
          </div>
        )
      },
    },
    {
      name: <span>Diubah Oleh</span>,
      selector: 'updatedBy',
      grow: 1,
      cell: (row) => {
        return (
          <div className="d-flex flex-column">
            <span className="m-0 fw-semibold">{row?.updatedBy || '-'}</span>
            <span className="m-0 text-muted" style={{ fontSize: '0.8rem' }}>
              {row?.updatedAt
                ? moment(row.updatedAt).format('DD MMM YYYY, HH:mm')
                : '-'}
            </span>
          </div>
        )
      },
    },
    {
      name: '',
      width: '150px',
      right: true,
      cell: (row) => {
        return (
          <div
            className="d-flex align-items-center"
            style={{ gap: '16px', flexGrow: 1 }}
          >
            <div id={`edit-${row?.id}`}>
              <Edit2
                color="#007BFF"
                size={18}
                onClick={() => {
                  setDataItemById(row)
                  setIsOpenModalAddEdit(true)
                }}
                style={{ cursor: 'pointer' }}
                data-testid={`findora-items-edit-${row?.id}`}
              />
            </div>
            <PMBTooltip target={`edit-${row?.id}`} text="Edit" />

            <div id={`remove-${row?.id}`}>
              <Trash2
                color="red"
                size={18}
                onClick={() => {
                  setIsOpenDeleteModal(true)
                  setDataItemById(row)
                }}
                style={{ cursor: 'pointer' }}
                data-testid={`findora-items-delete-${row?.id}`}
              />
            </div>
            <PMBTooltip target={`remove-${row?.id}`} text="Hapus" />
          </div>
        )
      },
    },
  ]

  // HANDLE SUBMIT REMOVE BY ID
  const handleSubmitRemoveById = async (id) => {
    try {
      if (!id) {
        enqueueSnackbar('Tidak ada data yang dipilih untuk dihapus', {
          variant: 'error',
        })
        return
      }

      const response = await deleteMasterItem(id)
      if (response && response?.data) {
        enqueueSnackbar(`Item "${dataItemById?.name}" berhasil dihapus`, {
          variant: 'success',
        })
        setIsOpenDeleteModal(false)
        setDataItemById(null)
        refetch()
      }
    } catch (error) {
      enqueueSnackbar('Item gagal dihapus', {
        variant: 'error',
      })
    }
  }

  const handleSort = (column, direction) => {
    dispatch(
      setFetchOption({
        sortby: column.sortField,
        sortdir: direction?.toUpperCase(),
      }),
    )
  }

  useEffect(() => {
    dispatch(setFetchOption({ page: 0 }))
  }, [searchInput])

  const debouncedSearch = useDebounce((value) => {
    dispatch(
      setFetchOption({
        page: 0,
        s: value,
      }),
    )
  }, 600)

  const onHandleSearchChange = (value) => {
    if (!value?.trim()) {
      setIsFilter(false)
    } else {
      setIsFilter(true)
    }
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && tag?.s.length === 0) {
      e.preventDefault()
    }
  }

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div>
      {/* MODAL IMAGE PREVIEW */}
      <PMBModalImagePreview
        isOpen={modalOpenImage}
        toggle={() => setModalOpenImage(!modalOpenImage)}
        images={imageItems}
      />

      {/* MODAL ADD/EDIT MASTER ITEM */}
      <ModalAddEditMasterItem
        isOpen={isOpenModalAddEdit}
        toggle={() => {
          setIsOpenModalAddEdit((prev) => !prev)
          setDataItemById(null)
        }}
        onSuccess={refetch}
        dataItemById={dataItemById}
        parentItems={parentItemsData?.items || []}
      />

      {/* MODAL REMOVE MASTER ITEM */}
      <PMBModalRemove
        isOpen={isOpenDeleteModal}
        onClose={() => {
          setIsOpenDeleteModal((prev) => !prev)
          setDataItemById(null)
        }}
        title={`Hapus Master Item`}
        dataId={dataItemById?.id}
        desc={`Apakah Anda yakin ingin menghapus item "${dataItemById?.name}" (${dataItemById?.code})?`}
        isLoading={isDeleting}
        onRemoveData={(id) => handleSubmitRemoveById(id)}
        buttonTitle="Hapus"
      />

      <DataTable
        responsive
        persistTableHead
        defaultSortFieldId={2}
        defaultSortAsc={true}
        columns={columns}
        data={data?.items || []}
        progressPending={isFetching}
        paginationTotalRows={data?.totalItems}
        pagination
        paginationServer
        paginationComponent={() => (
          <PMBPagination
            rowCount={data?.totalItems || 0}
            currentPage={tag?.page}
            rowsPerPage={tag?.size}
            setCurrentPage={(e) => {
              dispatch(setFetchOption({ page: e }))
            }}
            setRowsPerPage={(e) => {
              dispatch(setFetchOption({ size: e }))
            }}
            testId="findora-items-pagination"
          />
        )}
        progressComponent={
          <div style={{ margin: '100px 0px' }}>
            <Spinner />
          </div>
        }
        onSort={handleSort}
        sortIcon={<ChevronDown />}
        sortServer
        subHeader={true}
        subHeaderComponent={
          <div className="w-100">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div>
                <h6 className="fw-semibold mb-1">Daftar Master Item</h6>
                <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                  Kelola master item produk yang akan digunakan dalam skema
                </p>
              </div>

              <div className="d-flex gap-2">
                <Button
                  onClick={() => setIsOpenModalAddEdit(true)}
                  size="sm"
                  color="primary"
                  className="d-flex align-items-center gap-2"
                  data-testid="findora-items-add-button"
                >
                  <Plus size={16} />
                  Tambah Item
                </Button>
              </div>
            </div>

            <div className="my-4">
              <PMBSearchInputGroup
                label="Cari"
                value={searchInput}
                placeholder="Cari kode SKU atau nama komditas..."
                onChange={(e) => onHandleSearchChange(e.target.value)}
                maxWidth={isMobile ? '100%' : '20%'}
                onKeyDown={handleKeyDown}
                testId="findora-items-search-input"
              />
            </div>
          </div>
        }
        noDataComponent={
          <p className="my-3 fw-semibold">
            {isFilter
              ? 'Tidak ada data yang sesuai dengan pencarian Anda'
              : 'Belum ada master item yang tersedia'}
          </p>
        }
      />
    </div>
  )
}

export default TabMasterItems
